import { getTemplatesByDataType } from "../../../../../constants"
import { ConnectorDirection } from "../../../../../../types"

interface UseTemplateLoaderProps {
  dataType: "lead" | "subscription" | "pending_subscription"
  direction: ConnectorDirection
  setFormField: (name: string, val: any) => void
  currentConfig?: Record<string, any>
}

export const useTemplateLoader = ({
  dataType,
  direction,
  setFormField,
  currentConfig
}: UseTemplateLoaderProps) => {
  const availableTemplates = getTemplatesByDataType(dataType, direction)

  const loadTemplate = (templateName: string) => {
    const template = availableTemplates.find(t => t.name === templateName)

    if (!template) {
      return
    }

    // Templates are stored as CRM->BB
    // Format can be:
    // 1. Simple: {"crmField": "bb.field.path"}
    // 2. With transforms: {"crmField": {key: "bb.field.path", values: {...}}}

    if (direction === "incoming") {
      // Use template mapping as-is for incoming (CRM->BB)
      setFormField("field_mapping", template.field_mapping)
    } else {
      // Invert for outgoing: CRM->BB becomes BB->CRM
      const invertedMapping: Record<
        string,
        string | { key: string; values?: any }
      > = {}

      Object.entries(template.field_mapping).forEach(
        ([crmField, bbFieldOrConfig]) => {
          if (typeof bbFieldOrConfig === "string") {
            // Simple string mapping
            invertedMapping[bbFieldOrConfig] = crmField
          } else if (
            bbFieldOrConfig &&
            typeof bbFieldOrConfig === "object" &&
            "key" in bbFieldOrConfig
          ) {
            // New schema with key and optional values
            const bbFieldPath = bbFieldOrConfig.key
            if (bbFieldOrConfig.values) {
              // Preserve value transformations but invert the direction
              invertedMapping[bbFieldPath] = {
                key: crmField,
                values: bbFieldOrConfig.values
              }
            } else {
              // No value transforms, just invert the mapping
              invertedMapping[bbFieldPath] = crmField
            }
          }
        }
      )

      setFormField("field_mapping", invertedMapping)
    }

    setFormField("type", template.type)

    if (template.template) {
      setFormField("template", template.template)
    }

    if (template.content_type) {
      setFormField("config", {
        ...currentConfig,
        content_type: template.content_type
      })
    }
  }

  return {
    availableTemplates,
    loadTemplate
  }
}
